import { useCallback, useEffect, useRef, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { MWST_NORMALSATZ, normalisiereUid, uidFehler } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { useForm } from '../ui/hooks.js'
import { OrtsFeldPaar, useOrtschaften } from '../ui/OrtsFelder.js'
import { Zahlenfeld } from '../ui/Zahlenfeld.js'
import { ActionButton } from '../ui/ActionButton.js'
import { saveSettings, setTenantLogo, removeTenantLogo } from '../net/records.js'
import { logoUrl, api } from '../net/apiClient.js'
import { uuidv7 } from '../net/ids.js'
import { fehlerText } from '../net/errors.js'
import { db } from '../net/localDb.js'
import { Einrichtung } from './Einrichtung.js'
import { Kundenimport } from './Kundenimport.js'
import { Security } from './Security.js'
import { ZefixEinstellung } from '../ui/ZefixEinstellung.js'
import { MeinProfil } from '../ui/MeinProfil.js'
import { Anmeldungen } from '../ui/Anmeldungen.js'
import { Team } from './Team.js'

/**
 * Einstellungen – fünf Abschnitte statt einer Liste.
 *
 * Vorher stand alles untereinander auf einer Seite: Firmenname, Prüfstatus,
 * Logo, IBAN, Nummernkreis, Konditionen, Mitarbeiterpreise. Fachlich richtig,
 * zum Benutzen unbrauchbar – wer die IBAN nachtragen wollte, scrollte an
 * Feldern vorbei, die ihn nichts angingen, und wusste am Ende nicht, ob er
 * etwas verstellt hat.
 *
 * Die Reihenfolge folgt der Häufigkeit: Firma und Zahlung ändert ein Betrieb
 * beim Einrichten und dann selten; Texte gelegentlich; Sicherheit persönlich;
 * System fast nie. Der gefährlichste Wert – der Nummernkreis – steht deshalb
 * ganz hinten und nicht zwischen Adresse und Telefonnummer.
 *
 * Gespeichert wird pro Abschnitt, aber immer der ganze Satz: der Server nimmt
 * die Einstellungen als Ganzes entgegen. Was hier zählt, ist die Rückmeldung –
 * ein Satz, der sagt, was gespeichert wurde, und ein Fehler, der stehen bleibt
 * statt nach vier Sekunden zu verschwinden.
 */

interface SettingsForm {
  companyName: string
  street: string
  zip: string
  city: string
  uid: string
  iban: string
  qrIban: string
  bank: string
  email: string
  phone: string
  /** Ausdrückliche Antwort; null = noch nicht beantwortet. Siehe Migration 0032. */
  vatLiable: boolean | null
  defaultVatRate: number
  paymentTermDays: number
  quoteValidDays: number
  invoicePrefix: string
  nextInvoiceNumber: number
  numberCircleYearly: boolean
  quotePrefix: string
  nextQuoteNumber: number
  referenceType: 'QRR' | 'SCOR'
  qrZahlteil: boolean
  ansprechpartnerMail: 'person' | 'firma'
  ansprechpartnerTelefon: 'person' | 'firma'
  invoiceConditionsText: string
  quoteConditionsText: string
  workerSeesPrices: boolean
  legalName: string
  responsiblePersonName: string
  responsiblePersonEmail: string
  verificationStatus: 'unverified' | 'pending' | 'manually_verified' | 'rejected'
  verificationNote: string
}

const EMPTY: SettingsForm = {
  companyName: '',
  street: '',
  zip: '',
  city: '',
  uid: '',
  iban: '',
  qrIban: '',
  bank: '',
  email: '',
  phone: '',
  vatLiable: null,
  defaultVatRate: 8.1,
  paymentTermDays: 30,
  quoteValidDays: 30,
  invoicePrefix: 'RE-{mandant}-{jahr}-',
  nextInvoiceNumber: 1,
  numberCircleYearly: true,
  quotePrefix: 'OFF-{mandant}-{jahr}-',
  nextQuoteNumber: 1,
  referenceType: 'QRR',
  qrZahlteil: true,
  ansprechpartnerMail: 'person',
  ansprechpartnerTelefon: 'person',
  legalName: '',
  responsiblePersonName: '',
  responsiblePersonEmail: '',
  verificationStatus: 'unverified',
  verificationNote: '',
  invoiceConditionsText: '',
  quoteConditionsText: '',
  workerSeesPrices: false
}

/** «1.2 GB» / «860 MB» – dieselbe Schreibweise wie in den Meldungen des Servers. */
function menge(bytes: number): string {
  const GB = 1000 * 1000 * 1000
  if (bytes >= GB) return `${(bytes / GB).toFixed(1).replace('.0', '')} GB`
  if (bytes >= 1000 * 1000) return `${Math.round(bytes / (1000 * 1000))} MB`
  return `${Math.max(1, Math.round(bytes / 1000))} KB`
}

function str(v: unknown, fallback = ''): string {
  return v === null || v === undefined ? fallback : String(v)
}

/**
 * Wie die Nummer im nächsten Jahr aussieht.
 *
 * Für den erklärenden Satz unter dem Häkchen. Er zeigt die fertige Nummer und
 * nicht das Muster mit Platzhaltern: «RE-AVP-2027-1» beantwortet die Frage,
 * «RE-{mandant}-{jahr}-1» stellt eine neue.
 *
 * Dieselbe Ersetzung macht der Server beim Abschliessen – hier steht sie nur
 * für die Vorschau. Weicht sie je ab, ist die Vorschau falsch und nicht die
 * Nummer; deshalb wird hier nichts entschieden, sondern nur nachgezeichnet.
 */
function vorschau(prefix: string, jahr: number, kuerzel: string): string {
  return prefix.replace(/\{jahr\}/g, String(jahr)).replace(/\{mandant\}/g, kuerzel || '…')
}

/**
 * Datei als Base64 einlesen.
 *
 * FileReader und nicht fetch: die Datei liegt schon im Browser, es geht hier
 * nur um die Umwandlung. Der Netzaufruf passiert weiterhin ausschliesslich in
 * apiClient.ts.
 */
function leseAlsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('Die Datei liess sich nicht lesen.'))
    reader.readAsDataURL(file)
  })
}

const LOGO_MAX_BYTES = 1024 * 1024
const LOGO_TYPEN = ['image/png', 'image/jpeg', 'image/webp']

type Abschnitt =
  | 'einrichtung'
  | 'datenimport'
  | 'firma'
  | 'team'
  | 'zahlung'
  | 'texte'
  | 'mailversand'
  | 'handelsregister'
  | 'sicherheit'
  | 'system'

const ABSCHNITTE: Array<{ id: Abschnitt; label: string }> = [
  /*
    Die Einrichtung steht zuoberst, weil sie zuerst gebraucht wird und danach
    nie wieder. Ein Betrieb, der seit zwei Jahren arbeitet, sieht hier lauter
    Haken und liest weiter; ein Betrieb am ersten Tag findet von hier aus
    jeden anderen Reiter.
  */
  { id: 'einrichtung', label: 'Einrichtung' },
  /*
    Der Import steht neben der Einrichtung, weil er dort gebraucht wird: ein
    Betrieb liest seine Kundenliste in der ersten Woche ein und danach fast
    nie wieder.
  */
  { id: 'datenimport', label: 'Datenimport' },
  { id: 'firma', label: 'Firma' },
  /*
    Team steht direkt neben Firma, und das ist der ganze Punkt.

    Die beiden Reiter sind die zwei Ebenen: hier die Angaben des Betriebs, die
    auf jeden Beleg gehen – dort die Angaben der Personen, die auf keinen
    gehören. Nebeneinander gestellt erklärt sich der Unterschied von selbst;
    weit auseinander gestellt wird er wieder zu einer Regel, die man erklären
    muss.
  */
  { id: 'team', label: 'Team' },
  { id: 'zahlung', label: 'Zahlung' },
  { id: 'texte', label: 'Texte' },
  /*
    Ein eigener Abschnitt und kein Feld unter «System».

    Unter System stehen Nummernkreise und Bereinigungen – Dinge, die man
    einmal einstellt und dann nicht mehr anfasst. Der Handelsregister-Zugang
    ist etwas, das ein Betrieb sucht, weil ihm in der Kundenanlage ein Hinweis
    begegnet ist. Was gesucht wird, bekommt einen eigenen Namen im Menü.
  */
  /*
    Der Mailversand steht neben Handelsregister und nicht unter System.

    Beides sind Zugänge zu fremden Diensten, die ein Betrieb einmal einrichtet.
    Unter System stehen Nummernkreise und Bereinigungen – dort würde ihn
    niemand suchen.
  */
  { id: 'mailversand', label: 'E-Mail-Versand' },
  { id: 'handelsregister', label: 'Handelsregister' },
  { id: 'sicherheit', label: 'Sicherheit' },
  { id: 'system', label: 'System' }
]


/**
 * Stimmt die Prüfziffer dieser IBAN?
 *
 * Nach ISO 13616, dieselbe Rechnung, die auch die Bank macht: die ersten vier
 * Zeichen nach hinten, Buchstaben zu Zahlen, Rest bei Division durch 97 muss 1
 * sein. Dreissig Zeilen, kein Paket – das Frontend soll dafür keine Bibliothek
 * laden, und der Server prüft ohnehin ein zweites Mal.
 *
 * Bewusst KEINE Prüfung der Länge je Land: eine liechtensteinische oder
 * deutsche IBAN auf einer Schweizer Rechnung ist selten, aber nicht falsch,
 * und eine Liste von Länderlängen im Browser wäre eine Liste, die veraltet.
 */
function ibanPlausibel(eingabe: string): boolean {
  const wert = eingabe.replace(/\s/g, '').toUpperCase()
  if (!/^[A-Z]{2}[0-9A-Z]{13,32}$/.test(wert)) return false
  const gedreht = wert.slice(4) + wert.slice(0, 4)
  let rest = 0
  for (const zeichen of gedreht) {
    const ziffern = /[0-9]/.test(zeichen)
      ? zeichen
      : String(zeichen.charCodeAt(0) - 55) /* A=10 … Z=35 */
    for (const z of ziffern) rest = (rest * 10 + Number(z)) % 97
  }
  return rest === 1
}

/** Eine QR-IBAN erkennt man an der Institutsnummer: Stellen 5–9 liegen in 30000–31999. */
function istQrIban(eingabe: string): boolean {
  const wert = eingabe.replace(/\s/g, '').toUpperCase()
  const iid = Number(wert.slice(4, 9))
  return Number.isFinite(iid) && iid >= 30000 && iid <= 31999
}

/**
 * Taugt die Bankangabe für einen Schweizer QR-Zahlteil?
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum das ein Hinweis ist und keine Sperre
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Hier standen bis vor kurzem zwei Abbruchbedingungen: ohne QR-IBAN kein
 * Speichern bei QRR, und keine IBAN mit falscher Prüfziffer. Beides klang
 * vernünftig und war fachlich falsch. Eine Bankverbindung muss nicht
 * schweizerisch sein – wer ein Konto in Deutschland oder ausserhalb des
 * IBAN-Raums führt, konnte seine Stammdaten nicht mehr speichern.
 *
 * Deshalb steht hier jetzt ein Satz statt einer Sperre. Er sagt, was beim
 * Drucken passieren wird, und lässt den Betrieb trotzdem speichern. Die
 * verbindliche Prüfung macht der Server beim Rechnungsabschluss, mit
 * demselben Wortlaut – gerechnet wird sie hier nur nochmal, damit sie schon
 * beim Tippen dasteht und nicht erst nach dem Speichern.
 */
function qrHinweisFuer(werte: {
  iban: string
  qrIban: string
  referenceType: string
  qrZahlteil: boolean
}): string | null {
  /*
    Abgeschalteter Zahlteil: nichts zu warnen.

    Dann braucht die Rechnung keine Schweizer IBAN, sondern irgendeine
    Bankverbindung als Text – und das prüft der Abschluss.
  */
  if (!werte.qrZahlteil) {
    return werte.iban.trim()
      ? null
      : 'Ohne QR-Zahlteil druckt Raporto die Bankverbindung als Text auf die Rechnung. ' +
        'Dafür ist noch keine hinterlegt.'
  }
  const qrr = werte.referenceType === 'QRR'
  const konto = (qrr ? werte.qrIban : werte.iban).replace(/\s/g, '').toUpperCase()
  if (!konto) {
    return qrr
      ? 'Für die QR-Referenz ist noch keine QR-IBAN hinterlegt. Ohne sie lässt sich keine QR-Rechnung drucken.'
      : 'Es ist noch keine IBAN hinterlegt. Ohne sie lässt sich keine QR-Rechnung drucken.'
  }
  /*
    Drei Lagen, drei Sätze – nicht mehr einer für alles.

    Hier stand ein Sammelsatz: «braucht eine gültige Schweizer IBAN/QR-IBAN».
    Er traf auf eine IBAN mit falscher Prüfziffer genauso zu wie auf eine
    korrekte französische, und er nannte weder das eine noch das andere. Aus
    Produktion kam darauf die Rückfrage «das IST doch eine gültige CH-IBAN» –
    bei CH4512344567890088775, deren Prüfziffer nicht stimmt (Modulo 97
    ergibt 40, verlangt ist 1).

    Die richtige Prüfziffer wird bewusst nicht vorgeschlagen: aus einem
    Tippfehler würde sonst eine rechnerisch einwandfreie Kontonummer, die
    jemand anderem gehört.
  */
  const feld = qrr ? 'QR-IBAN' : 'IBAN'
  if (!ibanPlausibel(konto)) {
    return (
      `Die hinterlegte ${feld} ist keine gültige IBAN – die Prüfziffer stimmt nicht ` +
      '(Prüfung nach ISO 13616, Modulo 97). Meist ist eine Ziffer vertauscht oder zu viel. ' +
      'Bitte mit dem Kontoauszug abgleichen.'
    )
  }
  if (!/^(CH|LI)/.test(konto)) {
    return (
      `Die hinterlegte ${feld} ist eine gültige IBAN, aber keine schweizerische oder ` +
      `liechtensteinische (sie beginnt mit ${konto.slice(0, 2)}). Ein Schweizer QR-Zahlteil ` +
      'ist damit nicht möglich; ohne QR-Zahlteil lässt sich die Rechnung normal stellen.'
    )
  }
  if (qrr && !istQrIban(konto)) {
    return (
      'Die hinterlegte Nummer ist eine gültige Schweizer IBAN, aber keine QR-IBAN ' +
      '(die Institutsnummer liegt nicht im QR-Bereich 30000–31999). Mit der Referenzart QRR ' +
      'braucht es die QR-IBAN der Bank – oder auf Creditor Reference (SCOR) umstellen, dann ' +
      'genügt die normale IBAN.'
    )
  }
  if (!qrr && istQrIban(konto)) {
    return (
      'Im IBAN-Feld steht eine QR-IBAN. Mit SCOR weist die Bank diese Paarung zurück – ' +
      'bitte die normale IBAN eintragen oder auf QR-Referenz (QRR) umstellen.'
    )
  }
  return null
}

export function Settings({
  session,
  aufSession,
  startAbschnitt
}: {
  session: SessionUser
  aufSession?: (s: SessionUser) => void
  /**
   * Welcher Abschnitt beim Öffnen zuerst steht.
   *
   * Damit der Navigationseintrag «Team» direkt beim Team landet und nicht bei
   * den Firmenangaben, durch die man sich sonst erst durchsuchen müsste.
   */
  startAbschnitt?: Abschnitt
}): JSX.Element {
  const { values, set, reset } = useForm<SettingsForm>(EMPTY)
  const ortschaften = useOrtschaften()
  const [loaded, setLoaded] = useState(false)
  const [abschnitt, setAbschnitt] = useState<Abschnitt>(startAbschnitt ?? 'firma')
  const [logoKey, setLogoKey] = useState<string | null>(null)
  const [logoVersion, setLogoVersion] = useState(0)
  const [gewaehlt, setGewaehlt] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  /*
    Der Name der Kita-Gemeinschaft, falls dieser Betrieb zu einer gehört.

    Eine Auskunft für einen Satz, einmal geholt. Sie schaltet nichts frei und
    sperrt nichts: Die Felder in diesem Abschnitt bleiben die rechtlichen
    Angaben des Betriebs, und sie sollen bearbeitbar bleiben. Ohne Auskunft
    fehlt der Satz – das ist die richtige Vorgabe.
  */
  const [gemeinschaft, setGemeinschaft] = useState<string | null>(null)
  useEffect(() => {
    let weg = false
    void (async () => {
      try {
        const { dach } = await api.dach()
        if (!weg) setGemeinschaft(dach?.name ?? null)
      } catch {
        /* Kein Satz, kein Schaden. */
      }
    })()
    return () => {
      weg = true
    }
  }, [])
  /* Was zuletzt wirklich auf dem Server ankam – die Grundlage für «geändert». */
  const [gesichert, setGesichert] = useState<string>('')
  const dateiFeld = useRef<HTMLInputElement | null>(null)
  const actor = { tenantId: session.tenantId, userId: session.userId }

  /* Kennung und Jahr – gelesen, nicht bearbeitet, deshalb ausserhalb des Formulars. */
  const [mandantenkuerzel, setMandantenkuerzel] = useState('')
  const [mandantennummer, setMandantennummer] = useState('')
  const naechstesJahr = new Date().getFullYear() + 1

  /*
    Der Speicherstand.

    Er steht hier, weil ein voller Speicher sonst erst auffällt, wenn ein
    Monteur auf der Baustelle sein Foto nicht hochladen kann – und der kann
    von dort aus nichts daran ändern.
  */
  const [speicher, setSpeicher] = useState<{
    belegtBytes: number
    grenzeBytes: number
    fotosBytes: number
    belegeBytes: number
    paket: string
  } | null>(null)

  useEffect(() => {
    if (abschnitt !== 'system') return
    void api
      .speicherstand()
      .then(setSpeicher)
      .catch(() => setSpeicher(null))
  }, [abschnitt])

  function uebernehmen(row: Record<string, unknown>): SettingsForm {
    return {
      companyName: str(row.companyName),
      legalName: str(row.legalName),
      responsiblePersonName: str(row.responsiblePersonName),
      responsiblePersonEmail: str(row.responsiblePersonEmail),
      verificationStatus: (str(row.verificationStatus) ||
        'unverified') as SettingsForm['verificationStatus'],
      verificationNote: str(row.verificationNote),
      street: str(row.street),
      zip: str(row.zip),
      city: str(row.city),
      uid: str(row.uid),
      iban: str(row.iban),
      qrIban: str(row.qrIban),
      bank: str(row.bank),
      email: str(row.email),
      phone: str(row.phone),
      vatLiable: typeof row.vatLiable === 'boolean' ? row.vatLiable : null,
      defaultVatRate: Number(row.defaultVatRate ?? 8.1),
      paymentTermDays: Number(row.paymentTermDays ?? 30),
      quoteValidDays: Number(row.quoteValidDays ?? 30),
      invoicePrefix: str(row.invoicePrefix, 'RE-{mandant}-{jahr}-'),
      nextInvoiceNumber: Number(row.nextInvoiceNumber ?? 1),
      quotePrefix: str(row.quotePrefix, 'OFF-{mandant}-{jahr}-'),
      nextQuoteNumber: Number(row.nextQuoteNumber ?? 1),
      numberCircleYearly: row.numberCircleYearly !== false,
      referenceType: (row.referenceType as 'QRR' | 'SCOR') ?? 'QRR',
      // Fehlt das Feld (alter Zwischenspeicher), gilt «an» – so war es vorher.
      qrZahlteil: row.qrZahlteil !== false,
      ansprechpartnerMail: row.ansprechpartnerMail === 'firma' ? 'firma' : 'person',
      ansprechpartnerTelefon: row.ansprechpartnerTelefon === 'firma' ? 'firma' : 'person',
      invoiceConditionsText: str(row.invoiceConditionsText),
      quoteConditionsText: str(row.quoteConditionsText),
      workerSeesPrices: row.workerSeesPrices === true
    }
  }

  useEffect(() => {
    void db.settings.get('tenant').then((row) => {
      if (row) {
        const werte = uebernehmen(row as Record<string, unknown>)
        reset(werte)
        setGesichert(JSON.stringify(werte))
        setLogoKey(row.logoKey ? String(row.logoKey) : null)
        setMandantenkuerzel(str(row.mandantenkuerzel))
        setMandantennummer(str(row.mandantennummer))
      }
      setLoaded(true)
    })
    // reset ist stabil genug – der Bildschirm wird einmal gefüllt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
    Hat der Browser selbst etwas in die Felder geschrieben?

    ---------------------------------------------------------------------------
    Der Fall, der das nötig gemacht hat
    ---------------------------------------------------------------------------

    Ein Betrieb richtet sich im selben Browser als zweiter Mandant ein, öffnet
    die Firmenangaben – und findet Telefon und Mailadresse des ERSTEN Betriebs
    darin. Nicht, weil wir sie geschickt hätten: Chrome führt ein eigenes
    Adressbuch, merkt sich Strasse, PLZ, Ort, Telefon und Mail aus jedem
    Formular, das wie eine Adresse aussieht, und füllt sie beim nächsten
    gleichartigen Formular von selbst wieder ein.

    Das ist keine Kleinigkeit. Der Speicherknopf steht danach auf «Nicht
    gespeicherte Änderungen», und ein Klick trägt die Nummer eines fremden
    Betriebs als offizielle Nummer dieses Betriebs ein – die dann auf jeder
    Rechnung steht.

    autoComplete="off" steht auf allen Feldern und hilft gegen die
    Formularchronik. Gegen das Adressbuch von Chrome hilft es nicht zuverlässig;
    das ist eine Einstellung im Browser und gehört dem Benutzer, nicht uns.

    Was uns bleibt, ist der ehrliche Weg: sagen, dass es passiert ist. Browser
    markieren gefüllte Felder mit :-webkit-autofill; genau daran erkennt man
    sie, und zwar erst NACH dem Füllen, weshalb hier ein kurzer Takt läuft und
    keine einmalige Prüfung.
  */
  const [vomBrowserGefuellt, setVomBrowserGefuellt] = useState<string[]>([])
  /* Erst wenn der Betrieb hinschaut und zustimmt, geht Speichern wieder. */
  const [browserwerteGeprueft, setBrowserwerteGeprueft] = useState(false)

  useEffect(() => {
    if (abschnitt !== 'firma') {
      setVomBrowserGefuellt([])
      return
    }
    const pruefen = (): void => {
      const treffer: string[] = []
      const felder = Array.from(
        document.querySelectorAll<HTMLInputElement>('#abschnitt-firma input')
      )
      for (const feld of felder) {
        try {
          if (feld.matches(':-webkit-autofill')) {
            const label = document.querySelector<HTMLLabelElement>(`label[for="${feld.id}"]`)
            treffer.push(label?.textContent?.trim() ?? feld.id)
          }
        } catch {
          /* Browser ohne diesen Pseudo-Selektor: dann gibt es nichts zu melden. */
        }
      }
      setVomBrowserGefuellt(treffer)
    }
    setBrowserwerteGeprueft(false)
    pruefen()
    const takt = setInterval(pruefen, 700)
    /* Nach ein paar Sekunden ist die Sache entschieden – dann Ruhe. */
    const ende = setTimeout(() => clearInterval(takt), 6000)
    return () => {
      clearInterval(takt)
      clearTimeout(ende)
    }
  }, [abschnitt, loaded])

  if (session.role !== 'superuser') {
    return (
      <>
        <OfficeHead titel="Einstellungen" />
        <div className="o-card">
          <div className="o-leer">Diese Seite ist der Betriebsleitung vorbehalten.</div>
        </div>
      </>
    )
  }

  if (!loaded) {
    return (
      <>
        <OfficeHead titel="Einstellungen" />
        <div className="o-card">
          <div className="o-leer">Wird geladen …</div>
        </div>
      </>
    )
  }

  const geaendert = JSON.stringify(values) !== gesichert
  // Wird beim Tippen neu gerechnet, damit der Satz sofort dasteht.
  const qrHinweis = qrHinweisFuer(values)

  /**
   * Speichern.
   *
   * Immer der ganze Satz, weil der Server ihn so entgegennimmt – aber mit
   * einer Rückmeldung, die etwas aussagt. Vorher stand «Gespeichert» am Knopf
   * und verschwand; wer nebenbei telefonierte, hat es nie gesehen und wusste
   * hinterher nicht, ob er speichern musste. Jetzt bleibt der Zustand am
   * Bildschirm stehen, bis wieder etwas geändert wird.
   */
  async function speichern(): Promise<void> {
    setFehler(null)
    if (!values.companyName.trim()) {
      setFehler('Ohne Firmenname geht es nicht.')
      setAbschnitt('firma')
      return
    }
    /*
      Kein Halt an der Bankverbindung.

      Hier stand die Prüfziffer als Abbruchbedingung, und dazu die Forderung
      nach einer QR-IBAN bei QRR. Beides ist ersatzlos weg: die Stammdaten
      nehmen jede Bankverbindung an. Was daran für einen Schweizer Zahlteil
      fehlt, steht als Hinweis im Abschnitt Zahlung – gelesen, nicht erzwungen.
    */
    setBusy(true)
    try {
      const result = await saveSettings(actor, { ...values })
      /*
        Die Antwort des Servers gewinnt, nicht das Formular.

        Er darf Werte anpassen – die Nummer geht nur vorwärts. Übernähme der
        Bildschirm weiter seinen eigenen Stand, stünde dort eine Nummer, die
        nirgends gilt, und beim nächsten Laden eine andere.
      */
      const zurueck = uebernehmen(result.settings as Record<string, unknown>)
      reset(zurueck)
      setGesichert(JSON.stringify(zurueck))
      if (result.nextInvoiceNumberClamped) {
        notify.warn(
          `Gespeichert. Die nächste Rechnungsnummer wurde nicht zurückgesetzt – vergeben wird ab ${String(
            result.settings.nextInvoiceNumber
          )}.`
        )
      } else {
        notify.ok('Einstellungen gespeichert.')
      }
    } catch (err) {
      setFehler(fehlerText(err))
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  /*
    Der Riegel, und er ist der wichtigere Teil.

    Ein Hinweis, den man uebersieht, ist kein Schutz. Was hier passiert ist:
    der Browser traegt Werte ein, die Leiste sagt "Nicht gespeicherte
    Aenderungen", und ein Klick auf Speichern macht die Nummer und die
    Mailadresse eines FREMDEN Betriebs zu den offiziellen Angaben dieses
    Betriebs - die danach auf jeder Rechnung stehen.

    Gerade die Mailadresse ist das Heikle. Eine Nummer wie +41 44 000 00 00
    erkennt jeder als Platzhalter. Eine echte Adresse erkennt niemand als
    falsch - sie sieht aus wie eine richtige, weil sie eine ist. Nur gehoert
    sie jemand anderem.

    Deshalb ist Speichern gesperrt, bis jemand hingesehen und entschieden hat.
    Zwei Wege, beide ein Klick: verwerfen oder uebernehmen.
  */
  const browserband = vomBrowserGefuellt.length > 0 && !browserwerteGeprueft && (
    <div className="o-band alarm" id="s-autofill-warnung">
      <div>
        <strong>Dein Browser hat Felder ausgefuellt: {vomBrowserGefuellt.join(', ')}.</strong> Diese
        Werte kommen nicht aus Raporto, sondern aus dem Adressbuch deines Browsers — womoeglich von
        einem anderen Betrieb. Speichern ist gesperrt, bis du entschieden hast. Was du hier
        uebernimmst, steht danach auf jeder Rechnung.
        <div className="o-aktionen" style={{ marginTop: 10 }}>
          <button
            type="button"
            className="o-btn o-btn-primary"
            id="btn-autofill-verwerfen"
            onClick={() => {
              /* Zurueck auf das, was der Server hat - nicht auf leer. */
              if (gesichert) reset(JSON.parse(gesichert) as SettingsForm)
              setVomBrowserGefuellt([])
              setBrowserwerteGeprueft(true)
            }}
          >
            Vorschlaege verwerfen
          </button>
          <button
            type="button"
            className="o-btn o-btn-ghost"
            id="btn-autofill-uebernehmen"
            onClick={() => setBrowserwerteGeprueft(true)}
          >
            Werte sind richtig, uebernehmen
          </button>
        </div>
      </div>
    </div>
  )

  /* Solange nicht entschieden ist, bleibt der Speicherknopf zu. */
  const browserSperre = vomBrowserGefuellt.length > 0 && !browserwerteGeprueft

  const speicherleiste = (
    <div className="o-speicherleiste">
      <span className="o-soft" id="s-zustand">
        {fehler
          ? ''
          : browserSperre
            ? 'Gesperrt \u2013 der Browser hat Felder ausgef\u00fcllt'
            : geaendert
              ? 'Nicht gespeicherte \u00c4nderungen'
              : 'Alles gespeichert'}
      </span>
      <button
        className="o-btn o-btn-primary"
        id="btn-einstellungen-speichern"
        disabled={busy || !geaendert || browserSperre}
        onClick={() => void speichern()}
      >
        {busy ? 'Speichert …' : 'Speichern'}
      </button>
    </div>
  )

  return (
    <>
      <OfficeHead
        titel="Einstellungen"
        unter="Firmendaten, Zahlung, Texte und Zugang – alles, was auf einer Rechnung landet."
      />

      <div className="o-reiter" id="einstellungsreiter">
        {ABSCHNITTE.map((a) => (
          <button
            key={a.id}
            id={`tab-${a.id}`}
            className={a.id === abschnitt ? 'o-reiter-knopf aktiv' : 'o-reiter-knopf'}
            aria-current={a.id === abschnitt ? 'page' : undefined}
            onClick={() => setAbschnitt(a.id)}
          >
            {a.label}
          </button>
        ))}
      </div>

      {fehler && (
        <div className="o-band alarm" id="einstellungen-fehler">
          <span>{fehler}</span>
        </div>
      )}

      {/* ================= Firma ================= */}
      {abschnitt === 'einrichtung' && <Einrichtung />}

      {abschnitt === 'datenimport' && <Kundenimport />}

      {abschnitt === 'firma' && (
        <div id="abschnitt-firma">
          {browserband}
          <div className="o-card">
            <div className="o-card-head">
              <h3>Firma</h3>
              <span className="o-soft">
                Steht auf jeder Rechnung und jeder Offerte — nicht die Angaben einzelner Personen.
              </span>
            </div>
            <div className="o-pad">
              {/*
                Gehört dieser Betrieb zu einer Kita-Gemeinschaft, kommt der
                Briefkopf von dort – und genau das muss hier stehen, sonst
                sucht jemand den Kita-Namen in diesen Feldern und ändert
                stattdessen seinen Rechnungssteller.

                Die Felder bleiben bearbeitbar, und das ist Absicht: Was hier
                steht, ist die RECHTLICHE Identität dieses Bereichs. Sie
                gehört auf die Rechnung, in die UID-Zeile und in den
                QR-Zahlteil, wo der Name zum Konto passen muss.
              */}
              {gemeinschaft && (
                <p className="o-soft small" id="s-gemeinschaft-hinweis">
                  Dieser Bereich gehört zur Kita-Gemeinschaft{' '}
                  <strong>{gemeinschaft}</strong>. Kita-Name, Logo und Standortadresse
                  im Briefkopf kommen von dort und werden unter «Kita-Gemeinschaft»
                  gepflegt. Die Angaben hier sind deine rechtlichen Rechnungsdaten –
                  Firmenname, UID und Konto. Sie werden nicht überschrieben.
                </p>
              )}
              <div className="o-grid o-grid-2">
                <div className="o-field">
                  <label htmlFor="s-company">Firmenname</label>
                  <input
                    autoComplete="off"
                    id="s-company"
                    value={values.companyName}
                    onChange={(e) => set('companyName', e.target.value)}
                  />
                </div>
                {/*
                  Die MWST-Nummer stand hier, im Block «Betrieb», mit dem
                  Hinweis «leer lassen, wenn der Betrieb nicht MWST-pflichtig
                  ist». Damit wurde an dieser Stelle eine Steuerfrage
                  beantwortet, die weiter unten noch einmal gestellt wurde –
                  zwei Orte für dieselbe Sache, und der Benutzer musste sie
                  zusammenreimen. Sie steht jetzt bei der Mehrwertsteuer, dort,
                  wo die Pflicht beantwortet wird, und nur dann, wenn sie eine
                  Rolle spielt. Ein einmal erfasster Wert bleibt gespeichert,
                  auch wenn das Feld nicht sichtbar ist.
                */}
              </div>

              <div className="o-grid o-grid-3" style={{ marginTop: 16 }}>
                <div className="o-field" style={{ gridColumn: 'span 2' }}>
                  <label htmlFor="s-street">Strasse und Nummer</label>
                  <input
                    autoComplete="off"
                    id="s-street"
                    value={values.street}
                    onChange={(e) => set('street', e.target.value)}
                  />
                </div>
                <OrtsFeldPaar
                  praefix="s"
                  ids={{ plz: 's-zip', ort: 's-city' }}
                  plz={values.zip}
                  ort={values.city}
                  aufAenderung={({ plz, ort }) => {
                    set('zip', plz)
                    set('city', ort)
                  }}
                  ortschaften={ortschaften}
                  klassen={{ reihe: null, feld: 'o-field' }}
                />
                <div className="o-field">
                  {/*
                    «Offiziell» steht bewusst dabei. Ohne das Wort tippt
                    irgendwann jemand seine Handynummer hier hinein – und merkt
                    es erst, wenn ein Kunde ein halbes Jahr später darauf
                    anruft, weil sie auf einer Rechnung stand. Die eigene
                    Nummer steht unter «Sicherheit».
                  */}
                  <label htmlFor="s-phone">Firmentelefon</label>
                  <input
                    autoComplete="off"
                    id="s-phone"
                    inputMode="tel"
                    value={values.phone}
                    onChange={(e) => set('phone', e.target.value)}
                  />
                </div>
                <div className="o-field">
                  <label htmlFor="s-email">Firmenmailadresse</label>
                  <input
                    autoComplete="off"
                    id="s-email"
                    /*
                      Absichtlich kein type="email".

                      Chrome erkennt Adressfelder unter anderem am Typ und
                      bietet dann Eintraege aus seinem Adressbuch an. inputMode
                      gibt dem Handy trotzdem die richtige Tastatur - nur das
                      Adressbuch bleibt weg.
                    */
                    inputMode="email"
                    value={values.email}
                    onChange={(e) => set('email', e.target.value)}
                  />
                  <span className="hint">
                    Die Adresse des Betriebs, z.B. info@deinbetrieb.ch. Nicht deine Login-Adresse –
                    die steht unter «Sicherheit» und bleibt persönlich.
                  </span>
                </div>
              </div>

              {/*
                Wer auf dem Beleg als Ansprechpartner steht.

                Der Name kommt von der Person, die den Beleg abgeschlossen hat –
                das steht ohnehin in der Datenbank. Zu entscheiden ist nur, mit
                welcher Adresse und welcher Nummer sie erreichbar sein soll: mit
                ihrer eigenen oder mit der des Betriebs. Ein Einmannbetrieb
                wählt die Firmenangaben, ein Betrieb mit fünf Monteuren eher die
                persönlichen.
              */}
              <div className="office-section">Ansprechpartner auf Offerte und Rechnung</div>
              <div className="o-grid o-grid-2">
                <div className="o-field">
                  <label htmlFor="s-ap-mail">Welche Mailadresse</label>
                  <select
                    id="s-ap-mail"
                    value={values.ansprechpartnerMail}
                    onChange={(e) =>
                      set('ansprechpartnerMail', e.target.value as 'person' | 'firma')
                    }
                  >
                    <option value="person">Die persönliche Adresse der Person</option>
                    <option value="firma">Die Firmenmailadresse</option>
                  </select>
                </div>
                <div className="o-field">
                  <label htmlFor="s-ap-tel">Welche Telefonnummer</label>
                  <select
                    id="s-ap-tel"
                    value={values.ansprechpartnerTelefon}
                    onChange={(e) =>
                      set('ansprechpartnerTelefon', e.target.value as 'person' | 'firma')
                    }
                  >
                    <option value="person">Die persönliche Nummer der Person</option>
                    <option value="firma">Das Firmentelefon</option>
                  </select>
                  <span className="hint">
                    Fehlt die persönliche Angabe, steht die Firmenangabe auf dem Beleg.
                  </span>
                </div>
              </div>
            </div>
            {speicherleiste}
          </div>

          <div className="o-card" id="logo-block">
            <div className="o-card-head">
              <h3>Logo</h3>
              <span className="o-soft">PNG, JPG oder WEBP, höchstens 1 MB.</span>
            </div>
            <div className="o-pad">
              <div className="o-logo-zeile">
                <div className="o-logo-feld">
                  {logoKey ? (
                    <img
                      id="s-logo-vorschau"
                      src={logoUrl(`${logoKey}-${logoVersion}`)}
                      alt="Logo des Betriebs"
                    />
                  ) : (
                    <span className="o-soft">Noch kein Logo</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div className="o-field">
                    <label htmlFor="s-logo-datei">Datei wählen</label>
                    <input
                      autoComplete="off"
                      id="s-logo-datei"
                      ref={dateiFeld}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => setGewaehlt(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  <div className="office-actions" style={{ marginTop: 14 }}>
                    <button
                      className="o-btn o-btn-primary"
                      id="btn-logo-hochladen"
                      disabled={busy || !gewaehlt}
                      onClick={async () => {
                        if (!gewaehlt) return
                        setFehler(null)
                        if (gewaehlt.size > LOGO_MAX_BYTES) {
                          setFehler('Die Datei ist grösser als 1 MB.')
                          return
                        }
                        if (!LOGO_TYPEN.includes(gewaehlt.type)) {
                          setFehler('Erlaubt sind PNG, JPG und WEBP.')
                          return
                        }
                        setBusy(true)
                        try {
                          const bild = await leseAlsBase64(gewaehlt)
                          const s = await setTenantLogo(actor, bild, gewaehlt.name)
                          setLogoKey(s.logoKey ? String(s.logoKey) : null)
                          setLogoVersion((v) => v + 1)
                          setGewaehlt(null)
                          if (dateiFeld.current) dateiFeld.current.value = ''
                          notify.ok('Logo gespeichert. Neue Rechnungen tragen es ab sofort.')
                        } catch (err) {
                          setFehler(fehlerText(err))
                        } finally {
                          setBusy(false)
                        }
                      }}
                    >
                      Hochladen
                    </button>
                    {logoKey && (
                      <button
                        className="o-btn o-btn-ghost"
                        id="btn-logo-entfernen"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true)
                          try {
                            await removeTenantLogo(actor)
                            setLogoKey(null)
                            notify.info('Das Logo wurde entfernt.')
                          } catch (err) {
                            setFehler(fehlerText(err))
                          } finally {
                            setBusy(false)
                          }
                        }}
                      >
                        Entfernen
                      </button>
                    )}
                  </div>
                  <p className="o-soft" style={{ marginTop: 12 }}>
                    Das Logo wird sofort gespeichert – unabhängig vom Knopf «Speichern» oben.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= Team ================= */}
      {abschnitt === 'team' && (
        <Team
          session={session}
          firma={{
            name: values.companyName,
            street: values.street,
            zip: values.zip,
            city: values.city,
            uid: values.uid,
            phone: values.phone,
            email: values.email
          }}
        />
      )}

      {/* ================= Zahlung ================= */}
      {abschnitt === 'zahlung' && (
        <div className="o-card" id="abschnitt-zahlung">
          <div className="o-card-head">
            <h3>Zahlung</h3>
            <span className="o-soft">Bestimmt den QR-Zahlteil auf der Rechnung.</span>
          </div>
          <div className="o-pad">
            {/*
              Der Schalter steht ÜBER den Feldern, nicht darunter.

              Er entscheidet, was die Felder darunter überhaupt bedeuten: mit
              Zahlteil braucht es eine Schweizer IBAN und die passende
              Referenzart, ohne Zahlteil genügt irgendeine Bankverbindung. Wer
              zuerst die Felder ausfüllt und danach erfährt, dass die Hälfte
              davon nicht gilt, hat umsonst getippt.
            */}
            <div className="o-check" style={{ marginBottom: 18 }}>
              <input
                type="checkbox"
                id="s-qr-zahlteil"
                checked={values.qrZahlteil}
                onChange={(e) => set('qrZahlteil', e.target.checked)}
              />
              <label htmlFor="s-qr-zahlteil">
                Schweizer QR-Zahlteil auf der Rechnung
                <span className="hint">
                  {values.qrZahlteil
                    ? 'Die Rechnung bekommt den QR-Zahlteil. Dafür braucht es eine gültige Schweizer IBAN oder QR-IBAN.'
                    : 'Ohne Zahlteil: die Bankverbindung steht als Text auf der Rechnung, und bezahlt wird unter Angabe der Rechnungsnummer. Der Weg für Betriebe mit einem Konto ausserhalb der Schweiz.'}
                </span>
              </label>
            </div>

            <div className="o-grid o-grid-2">
              <div className="o-field">
                <label htmlFor="s-iban">IBAN</label>
                <input
                  autoComplete="off"
                  id="s-iban"
                  value={values.iban}
                  onChange={(e) => set('iban', e.target.value)}
                  placeholder="CH.. .... .... .... ...."
                />
                <span className="hint">
                  Auch eine ausländische Bankverbindung lässt sich hier hinterlegen.
                </span>
              </div>
              <div className="o-field">
                <label htmlFor="s-qriban">QR-IBAN</label>
                <input
                  autoComplete="off"
                  id="s-qriban"
                  value={values.qrIban}
                  onChange={(e) => set('qrIban', e.target.value)}
                />
                <span className="hint">Nur nötig für die Referenzart QRR.</span>
              </div>
              <div className="o-field">
                <label htmlFor="s-bank">Bank</label>
                <input
                  autoComplete="off"
                  id="s-bank"
                  value={values.bank}
                  onChange={(e) => set('bank', e.target.value)}
                />
              </div>
              <div className="o-field">
                <label htmlFor="s-reference">Referenzart</label>
                <select
                  id="s-reference"
                  value={values.referenceType}
                  disabled={!values.qrZahlteil}
                  onChange={(e) => set('referenceType', e.target.value as 'QRR' | 'SCOR')}
                >
                  <option value="QRR">QRR – QR-Referenz (braucht eine QR-IBAN)</option>
                  <option value="SCOR">SCOR – Creditor Reference (normale IBAN)</option>
                </select>
                <span className="hint">
                  {values.qrZahlteil
                    ? 'Beides sind gültige Schweizer QR-Rechnungen.'
                    : 'Ohne QR-Zahlteil gegenstandslos – die Referenz ist dann die Rechnungsnummer.'}
                </span>
              </div>
            </div>

            {/*
              Der Hinweis, der nicht sperrt.

              Er steht unter den Feldern und nicht als Fehlermeldung oben: was
              hier fehlt, hindert niemanden am Speichern. Es hindert nur am
              Drucken einer QR-Rechnung, und genau das steht da.
            */}
            {/*
              Der Stand in vier Zeilen, bevor der Satz dazu kommt.

              Vier Fragen wurden beim Lesen des Hinweises jedesmal neu
              gestellt: Ist der Zahlteil überhaupt an? Ist die Bankangabe
              gespeichert? Taugt sie für QR? Und wenn nicht – warum? Der eine
              Warnsatz beantwortete nur die letzte, und auch die nur vage.
              Jetzt stehen alle vier da, und zwar bevor jemand fragen muss.
            */}
            <div className="o-zahlstand" id="qr-stand">
              <span>
                <b>QR-Zahlteil</b>
                <i>{values.qrZahlteil ? 'aktiv' : 'abgeschaltet'}</i>
              </span>
              <span>
                <b>Bankangabe</b>
                <i>
                  {(values.referenceType === 'QRR' ? values.qrIban : values.iban).trim()
                    ? 'gespeichert'
                    : 'keine hinterlegt'}
                </i>
              </span>
              <span>
                <b>QR-tauglich</b>
                <i className={values.qrZahlteil && qrHinweis ? 'nein' : undefined}>
                  {!values.qrZahlteil
                    ? 'nicht nötig'
                    : qrHinweis
                      ? 'nein'
                      : 'ja'}
                </i>
              </span>
              <span>
                <b>Referenzart</b>
                <i>{values.referenceType === 'QRR' ? 'QRR · braucht QR-IBAN' : 'SCOR · normale IBAN'}</i>
              </span>
            </div>

            {/*
              Der Hinweis, der nicht sperrt – und ein Weg hinaus.

              Er steht unter den Feldern und nicht als Fehlermeldung oben: was
              hier fehlt, hindert niemanden am Speichern. Es hindert nur am
              Drucken einer QR-Rechnung.

              Daneben steht jetzt der zweite Ausweg als Knopf. «QR-Zahlteil
              deaktivieren» stand bisher nur als Satz im Hinweis, und wer ihn
              las, musste erst begreifen, dass damit das Kästchen weiter oben
              gemeint ist. Ein Satz, der eine Handlung beschreibt, die man
              woanders ausführen muss, ist eine Anleitung und keine Hilfe.
            */}
            {qrHinweis ? (
              <div className="o-band warnung" id="qr-hinweis" role="status">
                <span>{qrHinweis}</span>
                {values.qrZahlteil && (
                  <button
                    type="button"
                    id="btn-qr-aus"
                    className="o-btn"
                    onClick={() => set('qrZahlteil', false)}
                  >
                    QR-Zahlteil deaktivieren
                  </button>
                )}
              </div>
            ) : (
              <p className="hint" id="qr-hinweis-ok">
                {values.qrZahlteil
                  ? 'Diese Angaben reichen für eine Schweizer QR-Rechnung.'
                  : 'Diese Bankverbindung wird als Text auf die Rechnung gedruckt. Bezahlt wird unter Angabe der Rechnungsnummer.'}
              </p>
            )}

            <div className="office-section">Standardfristen</div>
            <div className="o-grid o-grid-3">
              <div className="o-field">
                <label htmlFor="s-term">Zahlungsfrist (Tage)</label>
                <Zahlenfeld
                  id="s-term"
                  wert={values.paymentTermDays}
                  aufWert={(n) => set('paymentTermDays', n)}
                />
                <span className="hint">
                  Daraus rechnet die Rechnung ihr «zahlbar bis» – Rechnungsdatum plus diese Tage.
                </span>
              </div>
              <div className="o-field">
                <label htmlFor="s-quote-days">Offertgültigkeit (Tage)</label>
                <Zahlenfeld
                  id="s-quote-days"
                  wert={values.quoteValidDays}
                  aufWert={(n) => set('quoteValidDays', n)}
                />
                <span className="hint">
                  Daraus rechnet die Offerte ihr «gültig bis». Ohne Gültigkeit bindet ein Angebot
                  den Betrieb unbegrenzt an einen Preis.
                </span>
              </div>
            </div>

            <div className="office-section">Mehrwertsteuer</div>
            {/*
              Die Steuerpflicht zuerst, der Satz danach.

              Hier stand ein Auswahlfeld mit drei Sätzen, und einer davon
              hiess «Keine MWST – nicht steuerpflichtig». Damit war eine
              Rechtslage eine Zeile in einer Satzliste: Wer nicht pflichtig
              ist, musste das aus einem Satz von 0 ableiten, und wer neu
              anfing, bekam 8.1 vorgesetzt, ohne je gefragt worden zu sein.

              Jetzt steht die Frage vorn und wird beantwortet. Aus der
              Antwort folgt, was darunter erscheint – und der Server rechnet
              nach: Wer «Ja» sagt, kommt ohne Nummer und ohne Satz nicht
              durch, und wer «Nein» sagt, dem wird der Satz auf 0 gesetzt,
              was immer im Feld stand.
            */}
            <div className="o-field">
              <span className="o-feldtitel">Ist der Betrieb mehrwertsteuerpflichtig?</span>
              <div className="o-janein" role="radiogroup" aria-label="Mehrwertsteuerpflicht">
                <label className="o-janein-zeile">
                  <input
                    autoComplete="off"
                    type="radio"
                    name="vat-liable"
                    id="s-vat-ja"
                    checked={values.vatLiable === true}
                    onChange={() => {
                      set('vatLiable', true)
                      /*
                        Von «nicht pflichtig» kommend steht der Satz auf 0,
                        und 0 ist bei Pflicht kein gültiger Wert. Dann der
                        Normalsatz als Vorschlag – einen eigenen Satz hat,
                        wer ihn braucht, gleich darunter.
                      */
                      if (!(Number(values.defaultVatRate) > 0))
                        set('defaultVatRate', MWST_NORMALSATZ)
                    }}
                  />
                  Ja, der Betrieb ist MWST-pflichtig
                </label>
                <label className="o-janein-zeile">
                  <input
                    autoComplete="off"
                    type="radio"
                    name="vat-liable"
                    id="s-vat-nein"
                    checked={values.vatLiable === false}
                    onChange={() => {
                      set('vatLiable', false)
                      set('defaultVatRate', 0)
                    }}
                  />
                  Nein, der Betrieb fakturiert ohne MWST
                </label>
              </div>
              {values.vatLiable === null && (
                <span className="hint" role="note">
                  Noch nicht beantwortet. Solange die Frage offen ist, weist Raporto auf neuen
                  Rechnungen und Offerten keine Mehrwertsteuer aus und zeigt keine MWST-Nummer.
                  Bereits abgeschlossene Belege ändern sich nicht.
                </span>
              )}
              {values.vatLiable === false && (
                <span className="hint">
                  Auf Rechnungen und Offerten steht dann keine MWST-Zeile und keine MWST-Nummer.
                  Bereits abgeschlossene Belege ändern sich nicht.
                </span>
              )}
            </div>

            {values.vatLiable === true && (
              <>
                {/*
                  Die Nummer ist bei Pflicht keine Kür.

                  Eine Rechnung, die Mehrwertsteuer ausweist, ohne die
                  Nummer zu nennen, unter der abgerechnet wird, ist für den
                  Empfänger nicht verwendbar. Geprüft wird die Form hier und
                  noch einmal im Server – dieselbe Regel, aus
                  `@raporto/shared`, damit nicht zwei Prüfungen
                  auseinanderlaufen.
                */}
                <div className="o-field">
                  <label htmlFor="s-uid">MWST-Nummer</label>
                  <input
                    autoComplete="off"
                    id="s-uid"
                    placeholder="CHE-123.456.789"
                    value={values.uid}
                    onChange={(e) => set('uid', e.target.value)}
                    onBlur={(e) => set('uid', normalisiereUid(e.target.value) || e.target.value)}
                    aria-invalid={uidFehler(values.uid) ? true : undefined}
                  />
                  <span className="hint">
                    {uidFehler(values.uid) ??
                      'Steht so auf jedem Beleg – mit dem Zusatz MWST, den Raporto beim Drucken setzt.'}
                  </span>
                </div>

                {/*
                  Gewählt, nicht getippt.

                  Ein freies Zahlenfeld lädt zu Werten ein, die es nicht
                  gibt – 8.5, 7.7 aus dem Vorjahr, 81 statt 8.1. Wer
                  wirklich einen anderen Satz braucht, etwa in einem Jahr,
                  in dem er wechselt, wählt «Anderer Satz» und tippt ihn;
                  dann ist es eine Entscheidung und kein Vertipper.
                */}
                <div className="o-field">
                  <label htmlFor="s-vat-wahl">MWST-Satz</label>
                  <select
                    id="s-vat-wahl"
                    value={
                      Number(values.defaultVatRate) === MWST_NORMALSATZ
                        ? String(MWST_NORMALSATZ)
                        : 'anderer'
                    }
                    onChange={(e) => {
                      if (e.target.value === 'anderer') {
                        // Beim Umschalten nichts überschreiben: der bisherige
                        // Satz bleibt stehen, bis jemand einen neuen tippt.
                        if (Number(values.defaultVatRate) === MWST_NORMALSATZ)
                          set('defaultVatRate', 3.8)
                      } else {
                        set('defaultVatRate', MWST_NORMALSATZ)
                      }
                    }}
                  >
                    <option value={String(MWST_NORMALSATZ)}>8.1 % – Normalsatz</option>
                    <option value="anderer">Anderer Satz</option>
                  </select>
                  {Number(values.defaultVatRate) !== MWST_NORMALSATZ && (
                    <div style={{ marginTop: 8 }}>
                      <label htmlFor="s-vat">Satz in Prozent</label>
                      <Zahlenfeld
                        id="s-vat"
                        nachkomma={1}
                        wert={values.defaultVatRate}
                        aufWert={(n) => set('defaultVatRate', n)}
                      />
                      {!(Number(values.defaultVatRate) > 0) && (
                        <span className="hint">
                          Bei Pflicht braucht es einen Satz über 0 %. Wer ohne MWST fakturiert,
                          wählt oben «Nein».
                        </span>
                      )}
                    </div>
                  )}
                  <span className="hint">
                    Gilt für alle Positionen auf Rechnungen und Offerten – eine Position kann
                    keinen eigenen Satz mehr haben. Bereits abgeschlossene Belege ändern sich
                    nicht.
                  </span>
                </div>
              </>
            )}
          </div>
          {speicherleiste}
        </div>
      )}

      {/* ================= Texte ================= */}
      {abschnitt === 'texte' && (
        <div className="o-card" id="abschnitt-texte">
          <div className="o-card-head">
            <h3>Texte auf den Belegen</h3>
            <span className="o-soft">
              Keine Preise in diese Felder – Preise gehören in Positionen.
            </span>
          </div>
          <div className="o-pad">
            {/*
              Zwei Felder und nicht eines.

              Ein gemeinsamer Text führte dazu, dass auf jeder Rechnung «Diese
              Offerte ist 30 Tage gültig» stand. Der Fehler erzeugt keine
              Fehlermeldung – man sieht ihn erst auf dem Papier beim Kunden.
            */}
            <div className="o-field">
              <label htmlFor="s-invoice-conditions">Konditionen auf Rechnungen</label>
              <textarea
                id="s-invoice-conditions"
                rows={5}
                value={values.invoiceConditionsText}
                placeholder="Zahlbar innert 30 Tagen ab Rechnungsdatum."
                onChange={(e) => set('invoiceConditionsText', e.target.value)}
              />
              <span className="hint">
                Beim Abschliessen wird der Text in den Beleg kopiert – eine spätere Änderung hier
                verändert bestehende Rechnungen nicht.
              </span>
            </div>

            <div className="o-field" style={{ marginTop: 20 }}>
              <label htmlFor="s-quote-conditions">Konditionen auf Offerten</label>
              <textarea
                id="s-quote-conditions"
                rows={5}
                value={values.quoteConditionsText}
                placeholder="Diese Offerte ist 30 Tage ab Ausstellungsdatum gültig."
                onChange={(e) => set('quoteConditionsText', e.target.value)}
              />
              <span className="hint">
                Ein fester Stundensatz in diesem Text wird bei der nächsten Preisrunde vergessen –
                und widerspricht dann den Positionen auf demselben Blatt.
              </span>
            </div>

            <p className="o-soft" style={{ marginTop: 18 }}>
              E-Mail-Texte kommen, sobald der Versand an einen echten Mailanbieter geht. Heute
              landet jede Nachricht als Datei im Ausgangsordner.
            </p>
          </div>
          {speicherleiste}
        </div>
      )}

      {/* ================= Handelsregister ================= */}
      {abschnitt === 'mailversand' && (
        <div id="abschnitt-mailversand">
          <MailVersand />
        </div>
      )}

      {abschnitt === 'handelsregister' && (
        <div id="abschnitt-handelsregister">
          <ZefixEinstellung />
        </div>
      )}

      {/* ================= Sicherheit ================= */}
      {abschnitt === 'sicherheit' && (
        <div id="abschnitt-sicherheit">
          {/*
            Die eigenen Angaben stehen bei der Sicherheit und nicht bei der
            Firma – weil sie zur Person gehören, genau wie Passwort und
            zweiter Faktor. Wer hier ist, ist bei sich selbst.
          */}
          <MeinProfil session={session} />
          {/*
            Die angemeldeten Geräte stehen ZWISCHEN den eigenen Angaben und
            dem Passwort, und das ist die richtige Reihenfolge: Wer ein Gerät
            verloren hat, sucht hier zuerst – und findet den Knopf, bevor er
            beim Passwortwechsel landet, der zwar auch alle Anmeldungen
            beendet, aber als Umweg.
          */}
          <Anmeldungen />
          <Security session={session} aufSession={aufSession} ohneKopf />
        </div>
      )}

      {/* ================= System ================= */}
      {abschnitt === 'system' && (
        <div id="abschnitt-system">
          <div className="o-card">
            <div className="o-card-head">
              <h3>Nummernkreise</h3>
              <span className="o-soft">Gehen nur vorwärts.</span>
            </div>
            <div className="o-pad">
              <div className="o-grid o-grid-4">
                <div className="o-field">
                  <label htmlFor="s-prefix">Rechnungen – Kreis</label>
                  <input
                    autoComplete="off"
                    id="s-prefix"
                    value={values.invoicePrefix}
                    onChange={(e) => set('invoicePrefix', e.target.value)}
                  />
                </div>
                <div className="o-field">
                  <label htmlFor="s-next">Nächste Rechnungsnummer</label>
                  <Zahlenfeld
                    id="s-next"
                    mindestens={1}
                    leerBeiNull={false}
                    wert={values.nextInvoiceNumber}
                    aufWert={(n) => set('nextInvoiceNumber', n)}
                  />
                </div>
                <div className="o-field">
                  <label htmlFor="s-quote-prefix">Offerten – Kreis</label>
                  <input
                    autoComplete="off"
                    id="s-quote-prefix"
                    value={values.quotePrefix}
                    onChange={(e) => set('quotePrefix', e.target.value)}
                  />
                </div>
                <div className="o-field">
                  <label htmlFor="s-quote-next">Nächste Offertnummer</label>
                  <Zahlenfeld
                    id="s-quote-next"
                    mindestens={1}
                    leerBeiNull={false}
                    wert={values.nextQuoteNumber}
                    aufWert={(n) => set('nextQuoteNumber', n)}
                  />
                </div>
              </div>
              <p className="o-soft" style={{ marginTop: 14 }}>
                Zwei Platzhalter sind erlaubt: <code>{'{mandant}'}</code> setzt dein Kürzel ein,
                <code> {'{jahr}'}</code> das laufende Jahr. Kommst du aus einer anderen Software und
                willst deine Reihe fortsetzen, lass sie weg und trage deinen Stand ein.
              </p>
              <p className="o-soft">
                Eine vergebene Nummer wird nie wiederverwendet. Zurücksetzen lässt sich der Zähler
                hier nicht – eine Lücke in der Rechnungsfolge muss jemand erklären können.
              </p>

              {/*
                Der Jahreswechsel, ausgeschrieben.

                «Nummernkreis jährlich erneuern» allein sagt einem Handwerker
                nicht, was am 1. Januar auf seiner Rechnung stehen wird. Also
                steht es da – mit der echten Jahreszahl und der echten Nummer,
                aus denselben Feldern darüber. Wer das Häkchen wegnimmt, liest
                sofort, was stattdessen gilt.
              */}
              <label className="o-check" style={{ marginTop: 18 }}>
                <input
                  autoComplete="off"
                  id="s-jahreskreis"
                  type="checkbox"
                  checked={values.numberCircleYearly}
                  onChange={(e) => set('numberCircleYearly', e.target.checked)}
                />
                <span>Nummernkreis jährlich erneuern</span>
              </label>
              <p className="o-soft" id="s-jahreskreis-satz" style={{ marginTop: 8 }}>
                {values.numberCircleYearly
                  ? `Ab dem 1. Januar ${naechstesJahr} beginnt der Zähler wieder bei 1: die erste Rechnung heisst ${vorschau(values.invoicePrefix, naechstesJahr, mandantenkuerzel)}1.`
                  : `Der Zähler läuft über den Jahreswechsel weiter: die erste Rechnung ${naechstesJahr} heisst ${vorschau(values.invoicePrefix, naechstesJahr, mandantenkuerzel)}${values.nextInvoiceNumber}.`}
              </p>
            </div>
            {speicherleiste}
          </div>

          {/*
            Wie viel Platz belegt ist.

            Mit Balken und nicht nur als Zahl: «3.9 GB von 5 GB» liest sich
            wie eine Buchhaltungszeile, ein zu drei Vierteln gefüllter Balken
            wie eine Warnung. Und aufgeschlüsselt, weil ein voller Speicher
            eine Ursache haben muss – fast immer sind es die Fotos.
          */}
          {speicher && (
            <div className="o-card">
              <div className="o-card-head">
                <h3>Speicher</h3>
                <span className="o-soft">Paket {speicher.paket}</span>
              </div>
              <div className="o-pad">
                <p className="o-fest" id="s-speicher">
                  {menge(speicher.belegtBytes)} von {menge(speicher.grenzeBytes)} belegt
                </p>
                <div
                  className={
                    speicher.belegtBytes / speicher.grenzeBytes > 0.9
                      ? 'o-balken voll'
                      : speicher.belegtBytes / speicher.grenzeBytes > 0.75
                        ? 'o-balken knapp'
                        : 'o-balken'
                  }
                  role="img"
                  aria-label={`${Math.round((speicher.belegtBytes / speicher.grenzeBytes) * 100)} Prozent belegt`}
                >
                  <i
                    style={{
                      width: `${Math.min(100, (speicher.belegtBytes / speicher.grenzeBytes) * 100)}%`
                    }}
                  />
                </div>
                <p className="o-soft" style={{ marginTop: 12 }}>
                  Fotos {menge(speicher.fotosBytes)} · Belege {menge(speicher.belegeBytes)}
                </p>
                <p className="o-soft">
                  Raporto ist die Ablage für Belege und Baustellenfotos, nicht das Laufwerk des
                  Betriebs. Ist der Platz voll, werden neue Fotos abgewiesen – Rechnungen und
                  Offerten schreibst du weiter.
                </p>
              </div>
            </div>
          )}

          {/*
            Unter welcher Kennung der Betrieb bei uns geführt wird.

            Er soll sie kennen, und zwar bevor er anruft: «AVP» am Telefon
            erspart beiden Seiten die Frage, welche Rechnung 501 gemeint ist.
            Ändern kann er sie nicht – eine Kennung, die sich ändert, findet
            eine alte Zahlung nicht mehr wieder.
          */}
          <div className="o-card">
            <div className="o-card-head">
              <h3>Deine Kennung bei Raporto</h3>
              <span className="o-soft">Ändert sich nie.</span>
            </div>
            <div className="o-pad">
              <div className="o-grid o-grid-2">
                <div className="o-field">
                  <label>Kürzel</label>
                  <p className="o-fest" id="s-mandantenkuerzel">
                    {mandantenkuerzel || '–'}
                  </p>
                </div>
                <div className="o-field">
                  <label>Mandantennummer</label>
                  <p className="o-fest" id="s-mandantennummer">
                    {mandantennummer || '–'}
                  </p>
                </div>
              </div>
              <p className="o-soft" style={{ marginTop: 10 }}>
                Nenne uns das Kürzel, wenn du dich meldest. Die Mandantennummer steckt in der
                Zahlungsreferenz jeder Rechnung – daran erkennt der Zahlungsabgleich später, dass
                eine eingegangene Zahlung zu dir gehört und nicht zu einem anderen Betrieb. Auf dem
                Beleg deines Kunden steht sie nur dort.
              </p>
            </div>
          </div>

          <div className="o-card">
            <div className="o-card-head">
              <h3>Mitarbeitergeräte</h3>
            </div>
            <div className="o-pad">
              <label className="o-check">
                <input
                  autoComplete="off"
                  id="s-worker-prices"
                  type="checkbox"
                  checked={values.workerSeesPrices}
                  onChange={(e) => set('workerSeesPrices', e.target.checked)}
                />
                <span>Mitarbeiter sehen Preise</span>
              </label>
              <p className="o-soft" style={{ marginTop: 10 }}>
                Standardmässig aus. Ist sie aus, werden Preise gar nicht erst auf ein
                Mitarbeitergerät übertragen – nicht nur ausgeblendet.
              </p>
            </div>
            {speicherleiste}
          </div>

          <div className="o-card" id="kyc-block">
            <div className="o-card-head">
              <h3>Firmenangaben und Prüfstatus</h3>
              <span className="o-soft">
                Vorbereitet, nicht an eine externe Prüfung angeschlossen.
              </span>
            </div>
            <div className="o-pad">
              <p className="o-muted" style={{ marginTop: 0 }}>
                {(() => {
                  const fehlt: string[] = []
                  if (!values.companyName.trim()) fehlt.push('Firmenname')
                  if (!values.street.trim() || !values.city.trim()) fehlt.push('Adresse')
                  if (!values.uid.trim()) fehlt.push('UID/MWST-Nummer')
                  if (!values.iban.trim() && !values.qrIban.trim()) fehlt.push('IBAN')
                  return fehlt.length > 0
                    ? `Noch nicht vollständig: ${fehlt.join(', ')}.`
                    : 'Firmenname, Adresse, UID und IBAN sind hinterlegt.'
                })()}
              </p>
              <p className="o-soft" id="kyc-status">
                {values.verificationStatus === 'manually_verified'
                  ? 'Status: manuell geprüft – jemand hat die Papiere gesehen.'
                  : values.verificationStatus === 'pending'
                    ? 'Status: Prüfung angestossen.'
                    : values.verificationStatus === 'rejected'
                      ? 'Status: Prüfung abgelehnt. Bitte Angaben korrigieren.'
                      : 'Status: Firmenangaben noch nicht verifiziert.'}
              </p>

              <div className="o-grid o-grid-2" style={{ marginTop: 18 }}>
                <div className="o-field">
                  <label htmlFor="s-legalname">Juristischer Name (Handelsregister)</label>
                  <input
                    autoComplete="off"
                    id="s-legalname"
                    value={values.legalName}
                    onChange={(e) => set('legalName', e.target.value)}
                    placeholder={values.companyName || undefined}
                  />
                </div>
                {/*
                  Hier stand ein Auswahlfeld, mit dem sich der Betrieb selbst
                  auf «manuell geprüft» setzen konnte. Eine Prüfung, die der
                  Geprüfte vergibt, ist keine – sie ist ein Feld, das aussieht
                  wie eine, und sie wäre genau dort gefährlich, wo man sich
                  später darauf verlässt.

                  Geblieben ist der Weg, der dem Betrieb zusteht: anfordern.
                  Alles Weitere entscheidet der Betreiber, nachdem er Papiere
                  gesehen hat.
                */}
                <div className="o-field">
                  <label>Prüfstatus</label>
                  <p className="o-fest" id="s-verifikation">
                    {values.verificationStatus === 'manually_verified'
                      ? 'manuell geprüft'
                      : values.verificationStatus === 'pending'
                        ? 'Prüfung angestossen'
                        : values.verificationStatus === 'rejected'
                          ? 'abgelehnt'
                          : 'nicht verifiziert'}
                  </p>
                  {values.verificationStatus === 'unverified' && (
                    <button
                      type="button"
                      className="o-btn"
                      id="btn-pruefung-anfordern"
                      disabled={busy}
                      onClick={() => {
                        void (async () => {
                          setBusy(true)
                          setFehler(null)
                          try {
                            await api.pruefungAnfordern(uuidv7())
                            set('verificationStatus', 'pending')
                            notify.info('Prüfung angefordert. Wir melden uns.')
                          } catch (err) {
                            setFehler(fehlerText(err))
                          } finally {
                            setBusy(false)
                          }
                        })()
                      }}
                    >
                      Prüfung anfordern
                    </button>
                  )}
                  {values.verificationStatus === 'rejected' && (
                    <span className="o-soft">
                      Angaben korrigieren und beim Betreiber melden – eine erneute Prüfung lässt
                      sich hier nicht auslösen.
                    </span>
                  )}
                </div>
                <div className="o-field">
                  <label htmlFor="s-verantwortlich">Verantwortliche Person</label>
                  <input
                    autoComplete="off"
                    id="s-verantwortlich"
                    value={values.responsiblePersonName}
                    onChange={(e) => set('responsiblePersonName', e.target.value)}
                  />
                </div>
                <div className="o-field">
                  <label htmlFor="s-verantwortlich-mail">E-Mail der verantwortlichen Person</label>
                  <input
                    autoComplete="off"
                    id="s-verantwortlich-mail"
                    /* Gleiche Begruendung wie bei der offiziellen Mailadresse. */
                    inputMode="email"
                    value={values.responsiblePersonEmail}
                    onChange={(e) => set('responsiblePersonEmail', e.target.value)}
                  />
                </div>
              </div>

              <div className="o-field" style={{ marginTop: 16 }}>
                <label htmlFor="s-verifikation-notiz">Bemerkung zur Prüfung</label>
                <input
                  autoComplete="off"
                  id="s-verifikation-notiz"
                  value={values.verificationNote}
                  onChange={(e) => set('verificationNote', e.target.value)}
                  placeholder="z.B. Handelsregisterauszug vom 12.09.2026 gesehen"
                />
              </div>
            </div>
            {speicherleiste}
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Der eigene Mailversand des Betriebs.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum das ein eigener Abschnitt ist
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Ohne diese Angaben kann Raporto eine Rechnung nicht direkt an den Kunden
 * schicken – nur an den Betrieb selbst, der sie dann weiterleitet. Das ist kein
 * technischer Mangel, sondern eine bewusste Grenze: ginge die Rechnung über das
 * Postfach von Raporto hinaus, stünde dort ein fremder Absender, und die
 * Antwort des Kunden landete bei uns statt beim Betrieb.
 *
 * Das Passwort kommt nie zurück. Das Feld ist deshalb beim Öffnen leer, und
 * leer heisst «unverändert» – nicht «löschen».
 */
function MailVersand(): JSX.Element {
  const [stand, setStand] = useState<Record<string, unknown> | null>(null)
  const [werte, setWerte] = useState({
    aktiv: false,
    host: '',
    port: 587,
    benutzer: '',
    passwort: '',
    absender: '',
    antwortAn: ''
  })
  const [fehler, setFehler] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  /*
    Gelesen wird aus demselben Zwischenspeicher wie die übrigen Einstellungen.

    Er wird beim Abgleich gefüllt und enthält die SMTP-Felder mit – ohne das
    Passwort, das der Server nie herausgibt. Ein eigener Netzaufruf wäre ein
    zweiter Weg zu denselben Daten und damit eine zweite Stelle, die veralten
    kann.
  */
  const laden = useCallback(async () => {
    /*
      Frisch vom Server, nicht aus dem Zwischenspeicher.

      Nach dem Speichern und nach dem Verbindungstest ändern sich Werte, die
      der Abgleich erst später mitbringt – «geprüft am» ist genau der, auf den
      jemand in dieser Sekunde schaut. Der Zwischenspeicher wird mitgezogen,
      damit der Rest des Bildschirms dasselbe sieht.
    */
    const antwort = await api.settingsLesen()
    const s = (antwort.settings ?? {}) as Record<string, unknown>
    if (antwort.settings) {
      await db.settings.put({ id: 'tenant', ...antwort.settings } as never)
    }
    setStand(s)
    setWerte({
      aktiv: s.smtpAktiv === true,
      host: str(s.smtpHost),
      port: Number(s.smtpPort ?? 587),
      benutzer: str(s.smtpBenutzer),
      passwort: '',
      absender: str(s.smtpAbsender),
      antwortAn: str(s.smtpAntwortAn)
    })
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  const passwortGesetzt = stand?.smtpPasswortGesetzt === true
  const geprueftAm = stand?.smtpGeprueftAm ? String(stand.smtpGeprueftAm) : null
  const letzterFehler = stand?.smtpLetzterFehler ? String(stand.smtpLetzterFehler) : null

  /*
    Stehen im Formular Änderungen, die noch nicht gespeichert sind?

    Das ist keine Feinheit, sondern der Unterschied zwischen «der Test sagt
    die Wahrheit» und «der Test prüft etwas anderes als das, was auf dem
    Bildschirm steht». «Verbindung testen» ruft den Server auf, und der liest
    den Zugang aus der Datenbank – nicht aus diesem Formular. Wer sein
    Passwort eintippt und direkt auf Testen drückt, prüft also den alten
    Stand und bekommt «Login abgelehnt», obwohl das neue Passwort stimmt.

    Genau dieser Weg ist einem Betrieb passiert: dreimal getestet, dreimal
    abgelehnt, das Passwort war korrekt – nur nie gespeichert.

    Deshalb sind die Testknöpfe gesperrt, solange etwas offen ist. Ein Hinweis
    daneben wäre zu leise; hier wird eine falsche Auskunft verhindert und
    nicht erklärt.
  */
  const t = (w: unknown): string => str(w).trim()
  const ungespeichert =
    werte.passwort.trim() !== '' ||
    werte.aktiv !== (stand?.smtpAktiv === true) ||
    t(werte.host) !== t(stand?.smtpHost) ||
    werte.port !== Number(stand?.smtpPort ?? 587) ||
    t(werte.benutzer) !== t(stand?.smtpBenutzer) ||
    t(werte.absender) !== t(stand?.smtpAbsender) ||
    t(werte.antwortAn) !== t(stand?.smtpAntwortAn)

  return (
    <div className="o-card" id="mailversand-block">
      <div className="o-card-head">
        <h3>Eigener Mailversand</h3>
        <span className="o-soft">
          {geprueftAm
            ? `Verbindung geprüft am ${new Date(geprueftAm).toLocaleString('de-CH')}`
            : 'Noch nicht geprüft'}
        </span>
      </div>
      <div className="o-pad">
        <p className="o-muted" style={{ marginTop: 0 }}>
          Mit einem eigenen Mailserver gehen Offerten und Rechnungen direkt an deine Kunden –
          mit deiner Adresse als Absender, und die Antwort kommt bei dir an. Ohne ihn schickt
          Raporto dir das PDF an deine eigene Adresse, und du leitest es selbst weiter.
        </p>

        {fehler && (
          <p className="o-band warnung" role="alert" id="smtp-fehler">
            {fehler}
          </p>
        )}
        {meldung && (
          <p className="o-band" role="status" id="smtp-meldung">
            {meldung}
          </p>
        )}
        {!fehler && letzterFehler && (
          <p className="o-band warnung" id="smtp-letzter-fehler">
            Letzter Versuch: {letzterFehler}
          </p>
        )}

        <div className="o-check" style={{ marginBottom: 16 }}>
          <input
            type="checkbox"
            id="smtp-aktiv"
            checked={werte.aktiv}
            onChange={(e) => setWerte({ ...werte, aktiv: e.target.checked })}
          />
          <label htmlFor="smtp-aktiv">
            Eigenen Mailserver verwenden
            <span className="hint">
              Abgeschaltet bleiben die Angaben erhalten – nur der Kundenversand ruht.
            </span>
          </label>
        </div>

        <div className="o-grid o-grid-2">
          <div className="o-field">
            <label htmlFor="smtp-host">Host</label>
            <input
              autoComplete="off"
              id="smtp-host"
              value={werte.host}
              placeholder="mail.deinanbieter.ch"
              onChange={(e) => setWerte({ ...werte, host: e.target.value })}
            />
          </div>
          <div className="o-field">
            <label htmlFor="smtp-port">Port</label>
            <input
              autoComplete="off"
              id="smtp-port"
              inputMode="numeric"
              value={String(werte.port)}
              onChange={(e) => setWerte({ ...werte, port: Number(e.target.value) || 0 })}
            />
            <span className="hint">
              587 mit STARTTLS oder 465 verschlüsselt ab der ersten Sekunde. Unverschlüsselt
              sendet Raporto nicht.
            </span>
          </div>
          <div className="o-field">
            <label htmlFor="smtp-benutzer">Benutzername</label>
            <input
              autoComplete="off"
              id="smtp-benutzer"
              value={werte.benutzer}
              placeholder="info@deinbetrieb.ch"
              onChange={(e) => setWerte({ ...werte, benutzer: e.target.value })}
            />
          </div>
          <div className="o-field">
            <label htmlFor="smtp-passwort">Passwort</label>
            <input
              id="smtp-passwort"
              type="password"
              autoComplete="new-password"
              value={werte.passwort}
              placeholder={passwortGesetzt ? '•••••••• (hinterlegt)' : ''}
              onChange={(e) => setWerte({ ...werte, passwort: e.target.value })}
            />
            <span className="hint">
              {passwortGesetzt
                ? 'Ein Passwort ist hinterlegt. Leer lassen heisst: unverändert.'
                : 'Wird verschlüsselt gespeichert und nie wieder angezeigt.'}
            </span>
          </div>
          <div className="o-field">
            <label htmlFor="smtp-absender">Absenderadresse</label>
            <input
              autoComplete="off"
              id="smtp-absender"
              inputMode="email"
              value={werte.absender}
              onChange={(e) => setWerte({ ...werte, absender: e.target.value })}
            />
            <span className="hint">Diese Adresse sieht der Kunde in seinem Postfach.</span>
          </div>
          <div className="o-field">
            <label htmlFor="smtp-antwort">Antwortadresse</label>
            <input
              autoComplete="off"
              id="smtp-antwort"
              inputMode="email"
              value={werte.antwortAn}
              onChange={(e) => setWerte({ ...werte, antwortAn: e.target.value })}
            />
            <span className="hint">Dorthin geht die Antwort des Kunden. Leer: die Absenderadresse.</span>
          </div>
        </div>

        <div className="office-actions" style={{ marginTop: 18 }}>
          <ActionButton
            id="btn-smtp-speichern"
            labels={{ idle: 'Speichern', busy: 'Speichert …', success: 'Gespeichert' }}
            onAction={async () => {
              setFehler(null)
              setMeldung(null)
              await api.smtpSpeichern(
                {
                  aktiv: werte.aktiv,
                  host: werte.host,
                  port: werte.port,
                  benutzer: werte.benutzer,
                  passwort: werte.passwort || undefined,
                  absender: werte.absender,
                  antwortAn: werte.antwortAn
                },
                uuidv7()
              )
              setWerte({ ...werte, passwort: '' })
              await laden()
              setMeldung('Gespeichert. Bitte die Verbindung testen, bevor du an Kunden sendest.')
            }}
            onError={(err: unknown) => setFehler(fehlerText(err))}
          />
          <ActionButton
            id="btn-smtp-test"
            variant="secondary"
            disabled={ungespeichert}
            labels={{ idle: 'Verbindung testen', busy: 'Testet …', success: 'Verbindung steht' }}
            onAction={async () => {
              setFehler(null)
              setMeldung(null)
              await api.smtpTesten({ testmail: false, ziel: 'benutzer' }, uuidv7())
              await laden()
              setMeldung('Die Verbindung steht. Ab jetzt ist der direkte Kundenversand möglich.')
            }}
            onError={(err: unknown) => setFehler(fehlerText(err))}
          />
          <ActionButton
            id="btn-smtp-testmail"
            variant="quiet"
            disabled={ungespeichert}
            labels={{ idle: 'Testnachricht an mich', busy: 'Sendet …', success: 'Gesendet' }}
            onAction={async () => {
              setFehler(null)
              setMeldung(null)
              await api.smtpTesten({ testmail: true, ziel: 'benutzer' }, uuidv7())
              await laden()
              setMeldung('Testnachricht gesendet. Sie geht nur an deine eigene Adresse.')
            }}
            onError={(err: unknown) => setFehler(fehlerText(err))}
          />
        </div>
        {ungespeichert && (
          <p className="hint" id="smtp-erst-speichern" style={{ marginTop: 10 }}>
            Zuerst speichern. Der Test prüft den hinterlegten Zugang, nicht das, was gerade im
            Formular steht.
          </p>
        )}
      </div>
    </div>
  )
}
